import { useRef, useState } from "react";
import { Loader2, Mic, Square } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function VoiceRecorder({ onTranscript }) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => chunksRef.current.push(e.data);
      recorder.onstop = async () => {
        setBusy(true);
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const formData = new FormData();
        formData.append("file", blob, "recording.webm");
        try {
          const { data } = await api.post("/voice/stt", formData, { headers: { "Content-Type": "multipart/form-data" } });
          if (data.text) onTranscript(data.text);
          else toast.error("Hmm, I couldn't hear that. Try again? 🎙️");
        } catch {
          toast.error("Voice transcription failed.");
        } finally {
          setBusy(false);
        }
        stream.getTracks().forEach((t) => t.stop());
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setRecording(true);
    } catch {
      toast.error("We need mic access to hear you speak!");
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
  };

  return (
    <button
      type="button"
      data-testid="mic-input-button"
      onClick={recording ? stopRecording : startRecording}
      disabled={busy}
      className={`relative w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center shrink-0 transition-all active:scale-90 ${
        recording
          ? "bg-gradient-to-br from-destructive to-secondary text-white shadow-lg"
          : "bg-gradient-to-br from-primary to-accent text-white shadow-md hover:scale-105"
      } disabled:opacity-60`}
      title={recording ? "Tap to stop" : "Tap me & speak your question!"}
    >
      {recording && (
        <>
          <span className="absolute inset-0 rounded-full bg-destructive/50 animate-ping" />
          <span className="absolute -inset-1.5 rounded-full border-2 border-destructive/40 animate-pulse" />
        </>
      )}
      <span className="relative z-10">
        {busy ? <Loader2 size={20} className="animate-spin" /> : recording ? <Square size={18} /> : <Mic size={20} />}
      </span>
    </button>
  );
}
